import { FormBuilder, FormGroup, Validators } from '@angular/forms'
import {
  addAttachmentsComplete,
  asLocalDate,
  asUTCEpoch,
  Attachment,
  AttachmentService,
  ConfirmDialogService,
  Destroyable,
  DialogService,
  getAttachments,
  isSomething,
  Rental,
  SaveForm,
  selectAllRentals,
  selectAttachmentsById,
  selectSome,
  Toaster,
  untilDestroy,
} from '@tokeet-frontend/tv3-platform'
import { select, Store } from '@ngrx/store'
import * as fromRoot from '@tv3/store/state'
import { filter, observeOn, switchMap, tap } from 'rxjs/operators'
import { asyncScheduler } from 'rxjs'
import { AttachmentResponse } from '@tv3/interfaces/responses/attachment.response'
import { AuthService } from '@tv3/services/auth.service'
import { OnInit, ViewChild, Component, Inject } from '@angular/core'
import { UploadBoxComponent, UploadBoxOptions } from '@tokeet-frontend/tv3-platform'
import { EntityAttributesComponent } from '@tokeet-frontend/custom-attrs'
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog'
import { Inquiry } from '@tv3/store/inquiry/inquiry.model'
import * as lodash from 'lodash'
import {
  selectAllExtraCategories,
  UpdateExtra,
  ExtraView,
  methods,
  AddExtra,
  selectExtraById,
  UpdateExtraComplete,
  DeleteExtra,
  categories,
} from '@tokeet-frontend/extras'
import { AmplitudeService } from '@tv3/services/amplitude.service'
import { Actions, ofType } from '@ngrx/effects'
import { LoadInquiryDetails } from '@tv3/store/inquiry/inquiry.actions'
import { selectInquiriesByIds } from '@tv3/store/inquiry/inquiry.selectors'
import { AddBillingDialogComponent } from '@tv3/shared/add-billing/add-billing-dialog.component'
import { ChargeDialogService } from '@tv3/containers/charges/charges/dialogs/charge-dialog/charge-dialog.service'
import { AddInquiryCardComplete } from '@tv3/store/inquiry/inquiry-fields.actions'
import { AddChargeComplete, ChargeResponse, selectChargesByExtraId } from '@tokeet-frontend/charges'

export enum ExtraDialogTabs {
  Extra,
  Recurring,
  Comments,
  Activity,
  Attributes,
}

export function getExtraPayloadFromForm(form: FormGroup) {
  const formData = form.getRawValue()
  return {
    rental_id: formData.rental_id,
    date: asUTCEpoch(formData.date),
    category: formData.category,
    amount: formData.amount,
    status: formData.status ? 2 : 1,
    method: formData.method,
    inquiry_id: formData.inquiry_id,
    description: formData.description,
  }
}

@Component({
  selector: 'app-extra-dialog',
  templateUrl: './extra-dialog.component.html',
  styleUrls: ['./extra-dialog.component.scss'],
  host: { class: 'modal-content' },
})
export class ExtraDialogComponent extends Destroyable implements OnInit {
  @ViewChild(UploadBoxComponent) uploadBox: UploadBoxComponent
  @ViewChild('attrs') attrs: EntityAttributesComponent
  isDuplicate = false
  isEdit = false

  extra: ExtraView

  tabs = ExtraDialogTabs
  activeTab = ExtraDialogTabs.Extra

  uploadBoxOptions: UploadBoxOptions = {
    maxSize: 10 * 1024 * 1024, // 10MB,
    maxUploads: 1,
    autoUpload: true,
    allowedFileExtensions: ['.png', '.jpg', '.jpeg', '.pdf', '.doc', '.docx', 'xlsx'],
    token: this.authService.token,
    guide: $localize`You can only upload one file that is 10MB or smaller, only PNG, JPG, PDF, WORD and EXCEL file types are allowed`,
  }

  form = this.fb.group({
    rental_id: [],
    category: [],
    inquiry_id: ['', [Validators.required]],
    date: ['', [Validators.required]],
    amount: ['', [Validators.required]],
    method: [''],
    description: ['', [Validators.minLength(2), Validators.maxLength(500)]],
    status: [false],
  })

  rentals$ = this.store.pipe(observeOn(asyncScheduler), select(selectAllRentals))
  methodOptions = methods

  categories$ = this.store.pipe(select(selectAllExtraCategories))

  attachments: Attachment[] = []

  isUploading = false
  lastUploadedAttachment: AttachmentResponse

  extraCharge: ChargeResponse
  extraBooking: Inquiry
  selectedBooking: Inquiry

  constructor(
    private fb: FormBuilder,
    private actions: Actions,
    private authService: AuthService,
    private dialog: DialogService,
    private toaster: Toaster,
    private attachmentService: AttachmentService,
    private store: Store<fromRoot.State>,
    private confirm: ConfirmDialogService,
    public dialogRef: MatDialogRef<ExtraDialogComponent>,
    @Inject(MAT_DIALOG_DATA)
    public data: {
      extra?: ExtraView
      duplicate?: boolean
      tab?: ExtraDialogTabs
      defaults?: { inquiry_id?: string; rental_id?: string }
    },
    private amplitudeService: AmplitudeService,
    private chargeDialogService: ChargeDialogService
  ) {
    super()
    this.extra = this.data.extra
    this.activeTab = this.data.tab || ExtraDialogTabs.Extra
    this.isDuplicate = this.data.duplicate
    this.isEdit = !this.isDuplicate && !!this.extra?.pkey
  }

  ngOnInit(): void {
    if (!this.extra && this.data.defaults) {
      this.form.patchValue({
        inquiry_id: this.data.defaults.inquiry_id || '',
        rental_id: this.data.defaults.rental_id || '',
      })
    }

    if (this.extra) {
      this.setFormData(this.extra)
      const extra$ = this.store.pipe(selectSome(selectExtraById(this.extra.pkey)))
      extra$
        .pipe(
          tap((t) => (this.extra = t)),
          filter((t) => !!t.inquiry_id),
          tap((item) => this.store.dispatch(LoadInquiryDetails({ id: item.inquiry_id }))),
          switchMap((item) => this.store.pipe(select(selectInquiriesByIds([item.inquiry_id])))),
          untilDestroy(this)
        )
        .subscribe(([booking]) => {
          this.extraBooking = booking
          this.selectedBooking = booking
        })
      this.store.dispatch(getAttachments({ ids: this.extra.attachments || [] }))

      extra$
        .pipe(switchMap((item) => this.store.pipe(select(selectAttachmentsById(item.attachments || [])))))
        .subscribe((attachments) => (this.attachments = attachments))

      this.store.pipe(select(selectChargesByExtraId(this.extra.pkey)), untilDestroy(this)).subscribe((items) => {
        this.extraCharge = lodash.head(items)
      })
    }

    this.actions.pipe(ofType(AddChargeComplete), untilDestroy(this)).subscribe(({ charge }) => {
      this.store.dispatch(
        UpdateExtraComplete({ update: { id: charge.extra_id, changes: { status: 2, statusView: 'Paid' } } })
      )
      this.close()
    })

    this.actions.pipe(ofType(AddInquiryCardComplete), untilDestroy(this)).subscribe(() => {
      this.toaster.success($localize`You can add charge with the saved card now`)
    })
  }

  onFileUploaded(data: any[]) {
    const attachments = lodash.map(data, (d) => Attachment.deserialize(d))
    this.store.dispatch(addAttachmentsComplete({ attachments }))
    this.attachments = [...this.attachments, ...attachments]
    if (this.isEdit) {
      this.store.dispatch(
        UpdateExtra({
          extraId: this.extra.pkey,
          payload: { attachments: this.attachments.map((t) => t.id) },
        })
      )
    }
  }

  onDownloadAttachment(item: Attachment) {
    this.attachmentService.downloadFile(item).subscribe()
  }

  onDeleteAttachment(item: Attachment) {
    this.confirm.confirm().subscribe(() => {
      this.attachments = lodash.filter(this.attachments, (i) => i.id !== item.id)
      this.store.dispatch(
        UpdateExtra({
          extraId: this.extra.pkey,
          payload: { attachments: this.attachments.map((t) => t.id) },
        })
      )
    })
  }

  onRentalChange([rental]: Rental[]) {
    if (this.selectedBooking?.rentalId !== rental?.id) {
      this.selectedBooking = null
      this.form.get('inquiry_id').reset()
    }
  }

  onSelectInquiry(item: Inquiry) {
    this.selectedBooking = item
    const rentalIdCtrl = this.form.get('rental_id')
    if (!rentalIdCtrl.value) {
      rentalIdCtrl.setValue(item.rentalId)
    }
  }

  setFormData(extra: ExtraView) {
    this.form.patchValue({
      ...extra,
      date: asLocalDate(extra.date, false) || '',
      status: extra.status === 2,
    })
  }

  onSaveAttrs() {
    this.attrs.save()
    this.close()
  }

  close() {
    this.dialogRef.close()
  }

  getRentalTooltip() {
    return this.isEdit
      ? $localize`Change the rental associated with this extra.`
      : $localize`Choose a Rental to link this extra to.`
  }

  onViewCharge() {
    this.chargeDialogService.open({ charge: this.extraCharge })
  }

  onCharge() {
    if (isSomething(this.extraBooking?.billing)) {
      this.chargeDialogService.open({
        defaults: {
          inquiry_id: this.extraBooking.id,
          extra_id: this.extra.pkey,
          amount: this.extra.amount,
          description: this.extra.description,
        },
      })
    } else {
      this.confirm
        .confirm({
          title: $localize`Billing Details is Missing`,
          body: $localize`Billing Details is not added on this booking, please add it first before you can add charge`,
          confirmText: $localize`Add Billing Details`,
        })
        .pipe(
          switchMap(() =>
            this.dialog
              .openFixedDialog(AddBillingDialogComponent, { width: '700px', data: { inquiry: this.extraBooking } })
              .afterClosed()
          )
        )
        .subscribe()
    }
  }

  onDelete() {
    this.confirm
      .confirm({
        title: $localize`Delete this extra?`,
        body: $localize`Are you sure you want to delete this extra?`,
      })
      .subscribe(() => {
        this.store.dispatch(DeleteExtra({ id: this.extra.pkey }))
        this.close()
      })
  }

  update() {
    this.store.dispatch(
      UpdateExtra({
        payload: {
          ...getExtraPayloadFromForm(this.form),
          attachments: this.attachments.map((t) => t.id),
        },
        extraId: this.extra.pkey,
      })
    )
  }

  add() {
    this.store.dispatch(
      AddExtra({
        payload: {
          ...getExtraPayloadFromForm(this.form),
          attachments: this.attachments.map((t) => t.id),
        },
      })
    )
    this.amplitudeService.logEvent('add-extras')
  }

  @SaveForm()
  save(form: FormGroup) {
    if (this.isEdit) {
      this.update()
    } else {
      this.add()
    }
    this.close()
  }
}
